import React from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { PageHero, CTASection } from "@/components/SitePage";
import { useLang } from "@/i18n";
import { Truck, Wrench, Gauge, Cpu, ScanLine, BarChart3, MapPin, RefreshCw, Bell, ShieldCheck, Boxes, Activity } from "lucide-react";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };

export default function Produkte() {
  const { lang } = useLang();
  const de = lang === "de";
  const t = (d, e) => (de ? d : e);

  const MODULES = [
    [Truck, t("Fahrzeuge & Flotte", "Vehicles & fleet"), t("Standort, Nutzung und Zustand Ihrer Fahrzeuge in Echtzeit – von Transportern bis LKW.", "Location, usage and condition of your vehicles in real time – from vans to trucks.")],
    [Wrench, t("Maschinen", "Machines"), t("Betriebsstunden, Wartungsfenster und Verfügbarkeit Ihrer Maschinen zentral im Blick.", "Operating hours, maintenance windows and availability of your machines at a glance.")],
    [Gauge, t("Messgeräte", "Measuring devices"), t("Kalibrierung, Prüffristen und Einsatzorte lückenlos dokumentiert.", "Calibration, inspection intervals and locations fully documented.")],
    [Cpu, t("IT-Geräte", "IT devices"), t("PCs, Laptops, Tablets und Mobilgeräte inkl. Softwarestand und Fernzugriff.", "PCs, laptops, tablets and mobile devices incl. software state and remote access.")],
    [ScanLine, t("Scanner & Peripherie", "Scanners & peripherals"), t("Seriennummern, Zustand und Zuordnung zu Standorten – automatisch erfasst.", "Serial numbers, condition and location assignment – captured automatically.")],
    [BarChart3, t("Berichte & Auswertungen", "Reports & analytics"), t("KPIs, Handlungsbedarf und Export – für fundierte Entscheidungen.", "KPIs, action items and export – for well-founded decisions.")],
  ];
  const FEATURES = [
    [Activity, t("Echtzeit-Status", "Real-time status"), t("Jedes Asset meldet online/offline, Zustand und Handlungsbedarf – im Ampelprinzip.", "Every asset reports online/offline, condition and action needed – traffic-light style.")],
    [RefreshCw, t("Automatische Updates", "Automatic updates"), t("Software- und Agent-Updates werden gestaffelt ausgerollt und überwacht.", "Software and agent updates are rolled out in stages and monitored.")],
    [MapPin, t("Standort-Management", "Location management"), t("Alle Standorte mit Adresse, Kontakt, Öffnungszeiten und zugeordneten Geräten.", "All locations with address, contact, opening hours and assigned devices.")],
    [Bell, t("Alerts & Tickets", "Alerts & tickets"), t("Störungen werden erkannt, priorisiert und als Ticket bis zur Lösung verfolgt.", "Incidents are detected, prioritised and tracked as a ticket until resolved.")],
    [Boxes, t("Lager & Rollout", "Stock & rollout"), t("Ersatzgeräte, Retouren und Neuzuweisungen – vom Lager bis an den Standort.", "Spares, returns and reassignments – from stock to the location.")],
    [ShieldCheck, t("Sicher & DSGVO-konform", "Secure & GDPR-compliant"), t("Rollen, Rechte und verschlüsselte Übertragung – Daten bleiben in Deutschland.", "Roles, permissions and encrypted transfer – data stays in Germany.")],
  ];
  const STEPS = [
    [t("Anbinden", "Connect"), t("Assets per Agent, Import oder manuell erfassen – in wenigen Minuten startklar.", "Add assets via agent, import or manually – ready in minutes.")],
    [t("Überwachen", "Monitor"), t("Live-Status, Zustand und Standort aller Geräte in einer Oberfläche.", "Live status, condition and location of all devices in one place.")],
    [t("Handeln", "Act"), t("Alerts, Tickets und Berichte führen gezielt zur richtigen Maßnahme.", "Alerts, tickets and reports lead you to the right action.")],
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "Inter, sans-serif" }}>
      <SiteNav />
      <PageHero eyebrow={t("PRODUKT", "PRODUCT")} title={t("Eine Plattform für", "One platform for")} highlight={t("alle Ihre Assets", "all your assets")} subtitle={t("FleetArea vereint Fahrzeuge, Maschinen, Messgeräte und IT-Geräte in einer Oberfläche – mit Echtzeit-Status, automatischen Updates und klaren Auswertungen.", "FleetArea unites vehicles, machines, measuring and IT devices in one interface – with real-time status, automatic updates and clear analytics.")} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs font-bold tracking-[0.2em] text-gray-400 mb-3" style={O}>{t("MODULE", "MODULES")}</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={O}>{t("Alles, was Sie verwalten – an einem Ort", "Everything you manage – in one place")}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {MODULES.map(([Icon, ti, d], i) => (
            <div key={i} data-testid={`produkt-modul-${i}`} className="group bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-[#c00000] transition-all">
              <span className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: RED }}><Icon className="w-6 h-6 text-white" /></span>
              <h3 className="text-lg font-bold mb-2" style={O}>{ti}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f7f8fa] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-[0.2em] text-gray-400 mb-3" style={O}>{t("SO FUNKTIONIERT'S", "HOW IT WORKS")}</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={O}>{t("In drei Schritten zur vollen Kontrolle", "Full control in three steps")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map(([ti, d], i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl p-7 hover:shadow-lg hover:border-[#c00000] transition-all">
                <div className="text-5xl font-extrabold" style={{ ...O, color: `${RED}22` }}>{i + 1}</div>
                <h3 className="text-lg font-bold -mt-4" style={O}>{ti}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs font-bold tracking-[0.2em] text-gray-400 mb-3" style={O}>{t("FUNKTIONEN", "FEATURES")}</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={O}>{t("Leistungsstark im Detail", "Powerful in detail")}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
          {FEATURES.map(([Icon, ti, d], i) => (
            <div key={i} className="flex gap-4">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${RED}12` }}><Icon className="w-5 h-5" style={{ color: RED }} /></span>
              <div><div className="text-sm font-bold" style={O}>{ti}</div><div className="text-sm text-gray-600 mt-1 leading-relaxed">{d}</div></div>
            </div>
          ))}
        </div>
      </section>

      <CTASection />
      <SiteFooter />
    </div>
  );
}
